'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

/**
 * The operator's working/display language for the console. English is the
 * default working language; the selector demonstrates the platform is
 * multilingual and sets the default target language for live content
 * translation (see useTranslation / LiveTranslateToggle).
 *
 * Mirrors the backend SUPPORTED_LANGUAGES (app/services/ai/translation_ai.py).
 */

export type LangCode =
  | 'en'
  | 'de'
  | 'es'
  | 'it'
  | 'fr'
  | 'nl'
  | 'pl'
  | 'sk'
  | 'cs'

export interface WorkingLanguage {
  code: LangCode
  name: string
  /** Short native/ISO label shown in the header pill. */
  label: string
}

export const WORKING_LANGUAGES: WorkingLanguage[] = [
  { code: 'en', name: 'English', label: 'EN' },
  { code: 'de', name: 'German', label: 'DE' },
  { code: 'es', name: 'Spanish', label: 'ES' },
  { code: 'it', name: 'Italian', label: 'IT' },
  { code: 'fr', name: 'French', label: 'FR' },
  { code: 'nl', name: 'Dutch', label: 'NL' },
  { code: 'pl', name: 'Polish', label: 'PL' },
  { code: 'sk', name: 'Slovak', label: 'SK' },
  { code: 'cs', name: 'Czech', label: 'CS' },
]

const STORAGE_KEY = 'supervity.workingLanguage'
const DEFAULT_LANG: LangCode = 'en'

export function languageName(code: string): string {
  return WORKING_LANGUAGES.find((l) => l.code === code)?.name || code.toUpperCase()
}

export function languageLabel(code: string): string {
  return WORKING_LANGUAGES.find((l) => l.code === code)?.label || code.toUpperCase()
}

interface LanguageContextValue {
  workingLanguage: LangCode
  setWorkingLanguage: (code: LangCode) => void
  languages: WorkingLanguage[]
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [workingLanguage, setWorkingLanguageState] = useState<LangCode>(DEFAULT_LANG)

  // Hydrate from localStorage on mount (client only).
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY)
      if (stored && WORKING_LANGUAGES.some((l) => l.code === stored)) {
        setWorkingLanguageState(stored as LangCode)
      }
    } catch {
      // localStorage unavailable — keep default.
    }
  }, [])

  const setWorkingLanguage = useCallback((code: LangCode) => {
    setWorkingLanguageState(code)
    try {
      window.localStorage.setItem(STORAGE_KEY, code)
    } catch {
      // ignore persistence failures
    }
  }, [])

  return (
    <LanguageContext.Provider
      value={{ workingLanguage, setWorkingLanguage, languages: WORKING_LANGUAGES }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    // Resilient default — the toggle/components still render if a consumer is
    // mounted outside the provider (e.g. the standalone Customer Portal shell).
    return {
      workingLanguage: DEFAULT_LANG,
      setWorkingLanguage: () => {},
      languages: WORKING_LANGUAGES,
    }
  }
  return ctx
}
